'use client'

import Link from 'next/link'
import { useState } from 'react'
import Layout from '~/src/components/Layout'
import ReportListCard from '~/src/components/reports/ReportListCard'
import type {
  PublishedResearchReportIndexItem,
  ResearchReportSubcategorySummary,
} from '~/src/db/queryPublishedResearchReport'
import { getReportSubcategoryLabel } from '~/src/lib/reports/categoryLabels'

const INITIAL_VISIBLE = 11
const PAGE_SIZE = 9

interface CategoryReportsPageContentProps {
  categorySlug: string
  categoryLabel: string
  subcategories: ResearchReportSubcategorySummary[]
  reports: PublishedResearchReportIndexItem[]
}

export default function CategoryReportsPageContent({
  categorySlug,
  categoryLabel,
  subcategories,
  reports,
}: CategoryReportsPageContentProps): React.ReactElement {
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE)
  const visibleReports = reports.slice(0, visibleCount)
  const hasMore = visibleCount < reports.length

  const featuredReports = visibleReports.slice(0, 2)
  const remainingReports = visibleReports.slice(2)

  return (
    <Layout hideBorders fullWidth>
      <div className='mx-auto flex w-full max-w-[1200px] flex-col gap-9 overflow-hidden px-6 pb-24 pt-9 md:px-12'>
        {/* Breadcrumb */}
        <nav
          aria-label='Breadcrumb'
          className='flex items-center gap-2 text-sm text-gray-500'
        >
          <Link
            href='/reports'
            className='hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
          >
            Reports
          </Link>
          <span aria-hidden='true'>/</span>
          <span className='text-gray-900'>{categoryLabel}</span>
        </nav>

        {/* Header */}
        <header className='flex flex-col gap-4'>
          <h1 className='font-display text-4xl font-bold leading-[44px] tracking-[-0.72px] text-gray-900 md:text-[60px] md:leading-[72px] md:tracking-[-1.2px]'>
            {categoryLabel} Reports
          </h1>
          <p className='max-w-4xl text-lg leading-7 text-gray-600'>
            AI-powered {categoryLabel.toLowerCase()} buying guides built from
            thousands of community reviews, expert opinions, and real-world
            data.
          </p>
          <div className='flex gap-4 text-base text-gray-500'>
            <span>
              {reports.length} report{reports.length === 1 ? '' : 's'}
            </span>
          </div>
          {subcategories.length > 0 && (
            <div className='flex flex-wrap gap-2 pt-2'>
              {subcategories.map((subcategory) => (
                <Link
                  key={subcategory.subcategorySlug}
                  href={`/reports/category/${categorySlug}/${subcategory.subcategorySlug}`}
                  className='rounded-full border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                >
                  {getReportSubcategoryLabel(
                    categorySlug,
                    subcategory.subcategorySlug
                  )}
                  <span className='ml-1 text-gray-400'>
                    {subcategory.reportCount}
                  </span>
                </Link>
              ))}
            </div>
          )}
        </header>

        {reports.length === 0 ? (
          <div className='rounded-2xl border bg-white px-5 py-8 text-sm text-gray-600'>
            No {categoryLabel.toLowerCase()} reports published yet.
          </div>
        ) : (
          <div className='flex flex-col gap-12'>
            {featuredReports.length > 0 && (
              <section aria-label='Featured reports'>
                <h2 className='sr-only'>
                  Featured {categoryLabel.toLowerCase()} reports
                </h2>
                <div className='grid gap-6 md:grid-cols-2'>
                  {featuredReports.map((report) => (
                    <ReportListCard
                      key={report.id}
                      report={report}
                      variant='large'
                    />
                  ))}
                </div>
              </section>
            )}

            {remainingReports.length > 0 && (
              <div className='grid gap-x-6 gap-y-12 md:grid-cols-3'>
                {remainingReports.map((report) => (
                  <ReportListCard
                    key={report.id}
                    report={report}
                    variant='standard'
                  />
                ))}
              </div>
            )}

            {hasMore && (
              <div className='flex items-center justify-center py-6'>
                <button
                  type='button'
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                  className='rounded-full bg-gray-100 px-5 py-3 text-base font-semibold text-gray-700 transition-colors hover:bg-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                >
                  Show more reports
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  )
}
